import type { AcpPlanData } from '@crowdex/agent-contracts';
import { cn } from '@crowdex/ui/lib/utils';
import { ArrowUp, Check, Circle, ListChecks, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { DesktopPlanPanel } from '@/components/desktop-message-part';
import { PanelEmpty } from '@/components/desktop-panel-primitives';
import type { PendingAnnotation } from '@/lib/desktop-shell/store';

/** How far the plan is: the steps done, out of all of them. */
export function planProgress(plan: AcpPlanData | null): { done: number; total: number } | null {
  if (plan === null || !('entries' in plan) || plan.entries.length === 0) return null;
  return {
    done: plan.entries.filter((entry) => entry.status === 'completed').length,
    total: plan.entries.length,
  };
}

/**
 * The Plan tab (D37): the steps of the agent's plan, the progress (3 / 5) and the step in progress
 * with the files it works on, then a field to ask the agent to change it. A plan written as a
 * document (markdown, a file) shows as such.
 */
export function PlanView({
  plan,
  onAnnotateSelection,
  onChangePlan,
}: {
  plan: AcpPlanData | null;
  onAnnotateSelection: (selection: PendingAnnotation) => void;
  /** Sends what the user wants changed to the agent. */
  onChangePlan?: ((text: string) => void) | undefined;
}) {
  const [draft, setDraft] = useState('');
  if (plan === null)
    return (
      <PanelEmpty
        icon={ListChecks}
        title="No plan submitted"
        text="Ask the active agent to share its current plan."
      />
    );
  if (!('entries' in plan))
    return (
      <div className="desktop-panel-view-enter mx-auto max-w-2xl">
        <DesktopPlanPanel data={plan} onAnnotateSelection={onAnnotateSelection} />
      </div>
    );
  const progress = planProgress(plan);
  return (
    <div className="desktop-panel-view-enter flex h-full min-h-0 flex-col">
      <header className="flex items-baseline justify-between gap-3 px-1 pb-2">
        <h2 className="type-card-title">Plan</h2>
        {progress !== null && (
          <span className="type-meta text-muted-foreground tabular-nums">
            {progress.done} / {progress.total}
          </span>
        )}
      </header>
      <ol aria-label="Plan steps" className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
        {plan.entries.map((entry, index) => {
          const running = entry.status === 'in_progress';
          const done = entry.status === 'completed';
          return (
            <li
              aria-current={running ? 'step' : undefined}
              className={cn(
                'flex items-start gap-3 rounded-2xl px-3 py-2',
                running && 'bg-control',
              )}
              key={entry.id ?? `${index}-${entry.content}`}
            >
              <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
                {done ? (
                  <Check aria-label="Done" className="size-4 text-success" />
                ) : running ? (
                  <LoaderCircle
                    aria-label="In progress"
                    className="size-4 animate-spin text-running"
                  />
                ) : (
                  <Circle aria-label="To do" className="size-4 text-muted-foreground" />
                )}
              </span>
              <span className="flex min-w-0 flex-col">
                <span
                  className={cn(
                    'type-ui',
                    done ? 'text-muted-foreground line-through' : 'text-foreground',
                  )}
                >
                  {entry.content}
                </span>
                {running && entry.files !== undefined && entry.files.length > 0 && (
                  <span className="type-meta truncate text-running">
                    Editing {entry.files.join(', ')}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {onChangePlan !== undefined && (
        <form
          className="mt-3 flex h-11 shrink-0 items-center gap-2 rounded-composer border border-border bg-card pr-1.5 pl-4"
          onSubmit={(event) => {
            event.preventDefault();
            const text = draft.trim();
            if (text.length === 0) return;
            onChangePlan(text);
            setDraft('');
          }}
        >
          <input
            aria-label="Change the plan"
            className="type-ui min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Change the plan…"
            value={draft}
          />
          <button
            aria-label="Send to the agent"
            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-control text-foreground disabled:opacity-40"
            disabled={draft.trim().length === 0}
            type="submit"
          >
            <ArrowUp className="size-4" />
          </button>
        </form>
      )}
    </div>
  );
}
